import { debounce } from '../core/utils/debounce.js';

let currentPopstateListener = null;

export function destroyFaqs() {
    if (currentPopstateListener) {
        window.removeEventListener('popstate', currentPopstateListener, true);
        currentPopstateListener = null;
    }
}

export function initializeFaqs(container = document) {
    destroyFaqs();

    const archiveSettingsEl = container.querySelector('.faq-archive');
    if (!archiveSettingsEl) return;

    if (archiveSettingsEl.dataset.faqsInitialized === 'true') {
        return;
    }
    archiveSettingsEl.dataset.faqsInitialized = 'true';

    const searchInput = container.querySelector('#faq-search');
    const categorySelect = container.querySelector('#faq-category');
    const items = Array.from(container.querySelectorAll('.faq-item'));
    const resultsContainer = container.querySelector('.faq-results');
    const paginationContainer = container.querySelector('.faq-pagination');
    
    const postsPerPage = parseInt(archiveSettingsEl.getAttribute('data-posts-per-page')) || -1;
    const paginationEnabled = archiveSettingsEl.getAttribute('data-pagination') === 'enabled';
    
    const noResultsText = "No results found.";
    let noResultsEl = container.querySelector('.faq-no-results');
    if (!noResultsEl && resultsContainer) {
        noResultsEl = document.createElement('div');
        noResultsEl.className = 'faq-no-results alert info';
        noResultsEl.textContent = noResultsText;
        noResultsEl.style.display = 'none';
        resultsContainer.appendChild(noResultsEl);
    }

    let currentPage = 1;
    let filteredItems = [...items];

    // Store original HTML for resetting highlights and set original index
    items.forEach((item, index) => {
        item.setAttribute('data-original-index', index);
        const titleSpan = item.querySelector('.accordion-group__trigger-label');
        item._originalTitle = titleSpan ? titleSpan.innerHTML : '';
        const contentDiv = item.querySelector('.accordion-group__panel-inner');
        item._originalContent = contentDiv ? contentDiv.innerHTML : '';
    });

    function escapeRegExp(string) {
        return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }

    function highlightText(node, regex) {
        if (node.nodeType === 3) {
            const match = node.data.match(regex);
            if (match) {
                const mark = document.createElement('mark');
                mark.className = 'faq-highlight';
                mark.style.backgroundColor = '#ffeb3b';
                mark.style.color = '#000';
                mark.style.padding = '0';
                
                const wordNode = node.splitText(match.index);
                wordNode.splitText(match[0].length);
                const clone = wordNode.cloneNode(true);
                mark.appendChild(clone);
                wordNode.parentNode.replaceChild(mark, wordNode);
                return 1; // skip the new element
            }
        } else if (node.nodeType === 1 && node.childNodes && !/(script|style)/i.test(node.tagName) && node.tagName !== 'MARK') {
            for (let i = 0; i < node.childNodes.length; i++) {
                i += highlightText(node.childNodes[i], regex);
            }
        }
        return 0;
    }

    // Read initial state from URL
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('search') && searchInput) {
        searchInput.value = urlParams.get('search');
    }
    if (urlParams.has('category') && categorySelect) {
        categorySelect.value = urlParams.get('category');
    }
    if (urlParams.has('page')) {
        currentPage = parseInt(urlParams.get('page')) || 1;
    }

    function updateURL(pushHistory = false) {
        const params = new URLSearchParams();
        if (searchInput && searchInput.value) params.set('search', searchInput.value);
        if (categorySelect && categorySelect.value) params.set('category', categorySelect.value);
        if (currentPage > 1) params.set('page', currentPage);
        
        const newUrl = window.location.pathname + (params.toString() ? '?' + params.toString() : '');
        if (pushHistory) {
            window.history.pushState({path: newUrl}, '', newUrl);
        } else {
            window.history.replaceState({path: newUrl}, '', newUrl);
        }
    }

    function filterItems(updateState = true, pushHistory = false) {
        let searchTerm = searchInput ? searchInput.value.toLowerCase().trim() : '';
        // Only match if the search term is 3 or more characters
        if (searchTerm.length < 3) {
            searchTerm = '';
        }
        
        const category = categorySelect ? categorySelect.value : '';

        const searchTerms = searchTerm.split(/\s+/).filter(w => w.length > 0);
        let regex = null;
        if (searchTerms.length > 0) {
            regex = new RegExp(`(${searchTerms.map(escapeRegExp).join('|')})`, 'i');
        }

        filteredItems = items.filter(item => {
            const searchText = item.getAttribute('data-search') || '';
            const categories = item.getAttribute('data-categories') ? item.getAttribute('data-categories').split(',') : [];
            
            // Fuzzy multi-word matching
            const matchesSearch = searchTerms.length === 0 || searchTerms.some(term => searchText.includes(term));
            const matchesCategory = !category || categories.includes(category);
            
            const isMatch = matchesSearch && matchesCategory;

            // Handle Highlighting
            const titleSpan = item.querySelector('.accordion-group__trigger-label');
            const contentDiv = item.querySelector('.accordion-group__panel-inner');
            
            if (titleSpan) titleSpan.innerHTML = item._originalTitle;
            if (contentDiv) contentDiv.innerHTML = item._originalContent;
            
            let score = 0;
            if (isMatch && searchTerms.length > 0) {
                const titleText = (item.getAttribute('data-title') || '').toLowerCase();
                // searchText already contains question + answer content from data-search
                searchTerms.forEach(term => {
                    const titleMatches = titleText.split(term).length - 1;
                    const contentMatches = searchText.split(term).length - 1;
                    // Weight title matches heavily (e.g. 10 points) vs general content matches (1 point)
                    score += (titleMatches * 10) + contentMatches;
                });
            }
            item._relevancyScore = score;

            if (isMatch && regex) {
                if (titleSpan) highlightText(titleSpan, regex);
                if (contentDiv) highlightText(contentDiv, regex);
            }
            
            return isMatch;
        });

        if (searchTerms.length > 0) {
            filteredItems.sort((a, b) => b._relevancyScore - a._relevancyScore);
        } else {
            filteredItems.sort((a, b) => parseInt(a.getAttribute('data-original-index')) - parseInt(b.getAttribute('data-original-index')));
        }

        if (updateState) {
            // Reset to page 1 on filter change
            currentPage = 1;
            updateURL(pushHistory);
        }
        render();
    }

    function render() {
        // Hide all items first
        items.forEach(item => item.style.display = 'none');

        if (filteredItems.length === 0) {
            if (noResultsEl) noResultsEl.style.display = 'block';
            if (paginationContainer) paginationContainer.innerHTML = '';
            return;
        } else {
            if (noResultsEl) noResultsEl.style.display = 'none';
        }

        // Reorder items in the DOM based on sorted filteredItems
        filteredItems.forEach(item => {
            resultsContainer.appendChild(item);
        });

        let totalPages = 1;
        let itemsToShow = filteredItems;

        if (paginationEnabled && postsPerPage > 0) {
            totalPages = Math.ceil(filteredItems.length / postsPerPage);
            if (currentPage > totalPages) currentPage = totalPages;
            
            const startIndex = (currentPage - 1) * postsPerPage;
            const endIndex = startIndex + postsPerPage;
            itemsToShow = filteredItems.slice(startIndex, endIndex);
        }

        itemsToShow.forEach(item => {
            item.style.display = ''; 
        });

        renderPagination(totalPages);
    }

    function renderPagination(totalPages) {
        if (!paginationContainer) return;
        paginationContainer.innerHTML = '';

        if (totalPages <= 1) return;

        const ul = document.createElement('ul');
        ul.className = 'page-numbers'; // WordPress default pagination class structure
        ul.style.display = 'flex';
        ul.style.listStyle = 'none';
        ul.style.gap = '10px';
        ul.style.padding = '0';
        ul.style.margin = '0';

        // Prev button
        if (currentPage > 1) {
            const li = document.createElement('li');
            li.innerHTML = `<a href="#" class="prev page-numbers" data-page="${currentPage - 1}">Previous</a>`;
            ul.appendChild(li);
        }

        for (let i = 1; i <= totalPages; i++) {
            const li = document.createElement('li');
            if (i === currentPage) {
                li.innerHTML = `<span class="page-numbers current">${i}</span>`;
            } else {
                li.innerHTML = `<a href="#" class="page-numbers" data-page="${i}">${i}</a>`;
            }
            ul.appendChild(li);
        }

        // Next button
        if (currentPage < totalPages) {
            const li = document.createElement('li');
            li.innerHTML = `<a href="#" class="next page-numbers" data-page="${currentPage + 1}">Next</a>`;
            ul.appendChild(li);
        }

        paginationContainer.appendChild(ul);

        // Add event listeners
        ul.querySelectorAll('a').forEach(link => {
            link.addEventListener('click', function(e) {
                e.preventDefault();
                currentPage = parseInt(this.getAttribute('data-page'));
                updateURL(true); // push history on pagination
                render();
                // Scroll to top of results smoothly
                if (resultsContainer) {
                    const scrollTarget = document.getElementById('results') || resultsContainer;
                    scrollTarget.scrollIntoView({behavior: 'smooth', block: 'start'});
                }
            });
        });
    }

    let isNewTypingSession = true;

    if (searchInput) {
        searchInput.addEventListener('focus', () => {
            isNewTypingSession = true;
        });

        const handleInput = debounce(() => {
            if (isNewTypingSession) {
                filterItems(true, true); // Push state on first keystroke to save previous URL
                isNewTypingSession = false;
            } else {
                filterItems(true, false); // Replace state on subsequent keystrokes
            }
        }, 150);

        searchInput.addEventListener('input', handleInput);

        searchInput.addEventListener('blur', () => {
            isNewTypingSession = true;
        });
    }

    if (categorySelect) {
        categorySelect.addEventListener('change', () => {
            filterItems(true, true); // Push state on category change
        });
    }

    // Capture initial pathname for popstate matching
    const faqPathname = window.location.pathname;

    // Handle browser history back/forward - using a named function so we can remove it if needed,
    // but using capture phase allows us to stop Barba from taking over the history change.
    function onPopState(e) {
        // If we are navigating within the same FAQ page path (i.e. just query string change)
        if (window.location.pathname === faqPathname) {
            // Stop Barba or other listeners from triggering a page load
            e.stopImmediatePropagation();
            
            const urlParams = new URLSearchParams(window.location.search);
            if (searchInput) searchInput.value = urlParams.get('search') || '';
            if (categorySelect) categorySelect.value = urlParams.get('category') || '';
            currentPage = parseInt(urlParams.get('page')) || 1;
            
            filterItems(false); // Re-filter based on restored URL params
        }
    }

    // Attach in capture phase (true) so it runs before Barba's listener
    currentPopstateListener = onPopState;
    window.addEventListener('popstate', currentPopstateListener, true);

    // Initial load filtering
    filterItems(false);
}
